import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import NoRecordFound from '../../../components/common/no-record-found';
import { FaPlus, FaEdit } from 'react-icons/fa';
const ROWS_PER_PAGE = 34;

const CommodityList = () => {
  const navigate = useNavigate();
  const [commodities, setCommodities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchCommodities = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: debouncedSearch.trim()
      });
      const res = await fetch(`${API_BASE_URL}/api/masters/commodity-get?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setCommodities(result.data || []);
      setTotal(result.total || 0);
    } catch (error) {
      setError('Failed to load data');
      console.error('Error fetching commodity records:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommodities();
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  // Pagination
  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="max-w-8xl mx-auto p-3 bg-white rounded-md shadow-sm border border-gray-300">
      <div className="flex flex-col sm:flex-row justify-between items-center pb-2 mb-3 border-b border-gray-200 gap-2">
        <h2 className="text-[15px] font-semibold text-gray-900 tracking-tight">Commodity Records</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
              <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search records..."
              value={search}
              onChange={handleSearchChange}
              className="pl-8 pr-2 py-1.5 border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-colors text-[12px] w-full sm:w-56 outline-none text-gray-900 placeholder-gray-500"
            />
          </div>
           <button
            className="flex items-center justify-center gap-1.5 bg-[#15803d] text-white px-3 py-1.5 text-[12px] font-semibold rounded-sm hover:bg-[#166534] transition-colors shadow-sm active:translate-y-[1px]"
            onClick={() => navigate('/commodity/new')}
          >
            <FaPlus className="text-[10px]" />
            Add New
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-center text-blue-600 py-6 text-[13px]">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-[13px]">{error}</div>
      ) : commodities.length === 0 ? (
        <NoRecordFound message="No commodity records found." />
      ) : (
        <div className="overflow-x-auto border border-gray-300 rounded-sm">
          <table className="min-w-full text-left border-collapse">
            <thead className="bg-[#f4f4f4] border-b border-gray-300">
              <tr>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">ID</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Name</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">HS Code</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Grade</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Moisture (%)</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Origin</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {commodities.map((item, idx) => (
                <tr key={item.commodity_id} className="hover:bg-blue-50/50 transition-colors duration-150 border-b border-gray-200 last:border-b-0">
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{item.commodity_id}</td>
                  <td className="px-3 py-1.5 text-gray-900 font-semibold text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{item.name}</td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{item.hs_code}</td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{item.grade}</td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{item.moisture_pct}</td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{item.origin}</td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 text-center">
                    <button
                      onClick={() => navigate(`/commodity/edit/${item.commodity_id}`)}
                      className="text-[12px] p-1.5 bg-gray-50 text-gray-600 rounded-sm hover:bg-green-50 hover:text-green-700 transition-colors border border-gray-200 hover:border-green-200 inline-block"
                      title="Edit Commodity"
                    >
                      <FaEdit className="text-[14px]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-3 text-[12px]">
          <div className="text-gray-600 mb-2 sm:mb-0">
            Showing <span className="font-semibold text-gray-900">{((page - 1) * ROWS_PER_PAGE) + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(page * ROWS_PER_PAGE, total)}</span> of <span className="font-semibold text-gray-900">{total}</span> records
          </div>
          <div className="inline-flex shadow-sm rounded-sm">
            <button
              onClick={handlePrevPage}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed font-medium rounded-l-sm transition-colors"
            >
              Previous
            </button>
            <div className="px-3 py-1 border-t border-b border-gray-300 bg-gray-50 text-gray-900 font-medium">
              {page} / {totalPages}
            </div>
            <button
              onClick={handleNextPage}
              disabled={page === totalPages}
              className="px-3 py-1 border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed font-medium rounded-r-sm transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommodityList;